import {
  copyCameraPoseAndFarPlane,
  useActiveCamera,
} from "@/components/r3f/cameras/active-camera-context";
import { CameraViewType } from "@/utils/cam-view";
import type { SupportedCamera } from "@faro-lotv/lotv";
import { useCallback, useMemo, useState } from "react";
import { OrthographicCamera, PerspectiveCamera } from "three";

type ChangeProjectionCbk = (
  newProjection: CameraViewType,
  cameraSize?: number,
) => void;

export type SingleViewCameras = {
  /** Default perspective camera */
  perspectiveCamera: PerspectiveCamera;
  /** Default orthographic camera */
  orthoCamera: OrthographicCamera;
  /** The currently active camera, either perspective or orthographic */
  camera: SupportedCamera;
  /** The current projection type of the camera */
  projection: CameraViewType;
  /** Callback to change the camera projection */
  changeProjection: ChangeProjectionCbk;
};

export type FlySplitSceneCameras = {
  /** The cameras and related information for the left view */
  leftViewCameras: SingleViewCameras;
  /** The cameras and related information for the right view */
  rightViewCameras: SingleViewCameras;
};

/**
 *
 * @returns An interface to manage the cameras to use in both views of a splitscreen scenario
 */
export function useFlySplitSceneCameras(): FlySplitSceneCameras {
  const {
    defaultOrthoCamera: leftOrthoCamera,
    defaultPerspectiveCamera: leftPerspectiveCamera,
  } = useActiveCamera();

  const rightPerspectiveCamera = useMemo(() => {
    const ret = leftPerspectiveCamera.clone();
    ret.name = "Right Perspective Camera";
    return ret;
  }, [leftPerspectiveCamera]);

  const rightOrthoCamera = useMemo(() => {
    const ret = leftOrthoCamera.clone();
    ret.name = "Right Orthographic Camera";
    // If the manual property is not set or is false,
    // R3F automatically changes the camera when resizing the canvas
    Object.assign(ret, { manual: true });
    return ret;
  }, [leftOrthoCamera]);

  // leftCamera can either be leftPerspectiveCamera or leftOrthoCamera
  const [leftCamera, setLeftCamera] = useState<SupportedCamera>(
    leftPerspectiveCamera,
  );
  // rightCamera can either be rightPerspectiveCamera or rightOrthoCamera
  const [rightCamera, setRightCamera] = useState<SupportedCamera>(
    rightPerspectiveCamera,
  );

  const leftProjection = useMemo(
    () =>
      leftCamera === leftPerspectiveCamera
        ? CameraViewType.perspective
        : CameraViewType.orthographic,
    [leftCamera, leftPerspectiveCamera],
  );

  const rightProjection = useMemo(
    () =>
      rightCamera === rightPerspectiveCamera
        ? CameraViewType.perspective
        : CameraViewType.orthographic,
    [rightCamera, rightPerspectiveCamera],
  );

  const changeLeftProjection = useCallback(
    (newProjection: CameraViewType, cameraSize?: number) => {
      if (newProjection === leftProjection) return;
      if (newProjection === CameraViewType.perspective) {
        copyCameraPoseAndFarPlane(leftOrthoCamera, leftPerspectiveCamera);
        leftPerspectiveCamera.aspect = orthoCameraAspect(leftOrthoCamera);
        leftPerspectiveCamera.updateProjectionMatrix();
        setLeftCamera(leftPerspectiveCamera);
      } else {
        copyCameraPoseAndFarPlane(leftPerspectiveCamera, leftOrthoCamera);
        setOrthoCameraSize(
          leftOrthoCamera,
          leftPerspectiveCamera.aspect,
          cameraSize,
        );
        setLeftCamera(leftOrthoCamera);
      }
    },
    [leftProjection, leftPerspectiveCamera, leftOrthoCamera],
  );

  const changeRightProjection = useCallback(
    (newProjection: CameraViewType, cameraSize?: number) => {
      if (newProjection === rightProjection) return;
      if (newProjection === CameraViewType.perspective) {
        copyCameraPoseAndFarPlane(rightOrthoCamera, rightPerspectiveCamera);
        rightPerspectiveCamera.aspect = orthoCameraAspect(rightOrthoCamera);
        rightPerspectiveCamera.updateProjectionMatrix();
        setRightCamera(rightPerspectiveCamera);
      } else {
        copyCameraPoseAndFarPlane(rightPerspectiveCamera, rightOrthoCamera);
        setOrthoCameraSize(
          rightOrthoCamera,
          rightPerspectiveCamera.aspect,
          cameraSize,
        );
        setRightCamera(rightOrthoCamera);
      }
    },
    [rightProjection, rightPerspectiveCamera, rightOrthoCamera],
  );

  const leftViewCameras = useMemo(
    () => ({
      perspectiveCamera: leftPerspectiveCamera,
      orthoCamera: leftOrthoCamera,
      camera: leftCamera,
      projection: leftProjection,
      changeProjection: changeLeftProjection,
    }),
    [
      leftPerspectiveCamera,
      leftOrthoCamera,
      leftCamera,
      leftProjection,
      changeLeftProjection,
    ],
  );

  const rightViewCameras = useMemo(
    () => ({
      perspectiveCamera: rightPerspectiveCamera,
      orthoCamera: rightOrthoCamera,
      camera: rightCamera,
      projection: rightProjection,
      changeProjection: changeRightProjection,
    }),
    [
      rightPerspectiveCamera,
      rightOrthoCamera,
      rightCamera,
      rightProjection,
      changeRightProjection,
    ],
  );

  return useMemo(
    () => ({
      leftViewCameras,
      rightViewCameras,
    }),
    [leftViewCameras, rightViewCameras],
  );
}

function setOrthoCameraSize(
  orthoCamera: OrthographicCamera,
  aspect: number,
  cameraSize?: number,
): void {
  if (cameraSize !== undefined) {
    const cameraHalfSize = cameraSize * 0.5;
    orthoCamera.top = cameraHalfSize;
    orthoCamera.bottom = -cameraHalfSize;
    orthoCamera.left = -cameraHalfSize * aspect;
    orthoCamera.right = cameraHalfSize * aspect;
    orthoCamera.updateProjectionMatrix();
  }
}

function orthoCameraAspect(orthoCamera: OrthographicCamera): number {
  return (
    (orthoCamera.right - orthoCamera.left) /
    (orthoCamera.top - orthoCamera.bottom)
  );
}
